/**
 * @file        src/components/AudienceTabs.jsx
 * @description The B2C / Corporate tab strip above a split screen.
 *
 *              THE TAB IS A ROUTE, NOT A PICKER, and that distinction is the
 *              whole point of this component. CLAUDE.md §9d retired the
 *              "Customer type" segment from the Dashboard and Reports because it
 *              was a FILTER: the audience lived in component state, a shared
 *              link no longer said which audience it meant, and — the reason it
 *              was found at all — it had been quietly hiding a bug, filtering
 *              the revenue chart while every tile beside it stayed
 *              whole-business.
 *
 *              These tabs NAVIGATE. Each is a distinct URL with its own fixed
 *              `audience` prop, so everything the split bought is intact: the
 *              audience cannot be overridden from the query string, a copied
 *              link lands on exactly the screen it came from, Back works, and
 *              each screen still asks the API for one audience only. There is
 *              deliberately no "All" tab — the API's enum is b2c|b2b, and adding
 *              a whole-business mode would rebuild precisely what §9d removed.
 *
 *              What changes is only that the two screens are now one click from
 *              each other instead of two sidebar entries apart.
 *
 *              Anchors, not buttons, for the same reason: a tab that cannot be
 *              copied or opened in a new tab is a filter wearing a tab's
 *              clothes. NavLink supplies `aria-current="page"` on the active
 *              one, which is the correct announcement for navigation — this is
 *              not an ARIA tablist and does not claim to be.
 * @author      Kinetika Labs
 * @created     2026-09-24
 * @changelog
 *   - 2026-09-24 Claude — Initial (combined Dashboard).
 */
import { NavLink } from 'react-router-dom';

/**
 * @param {Array<{to:string, label:string, end?:boolean}>} tabs
 *        `end` marks an index route — '/' must match exactly, or it stays
 *        active on every child path.
 * @param {string} [ariaLabel]
 */
export default function AudienceTabs({ tabs, ariaLabel = 'Customer type' }) {
  return (
    <nav className="seg audience-tabs" aria-label={ariaLabel}>
      {tabs.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end={t.end}
          className={({ isActive }) => (isActive ? 'active' : undefined)}
        >
          {t.label}
        </NavLink>
      ))}
    </nav>
  );
}
